import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { DateInputIT } from "@/components/ui/date-input-it";
import { errMsg } from "@/lib/errors";
import {
  FileSpreadsheet, FileDown, BookOpenCheck, Wallet, Download, Plus, Trash2, Play,
  Clock, Save, Sparkles,
} from "lucide-react";

const SOURCE_META = {
  bookings: { label: "Prenotazioni", icon: BookOpenCheck, color: "from-[#1E3A5F] to-[#2A4F7C]" },
  finance: { label: "Bilancio / Movimenti", icon: Wallet, color: "from-emerald-700 to-emerald-500" },
  timesheets: { label: "Timbrature", icon: Clock, color: "from-orange-700 to-orange-500" },
};

const FINANCE_TYPES = [
  { value: "all", label: "Tutti" },
  { value: "income", label: "Solo entrate" },
  { value: "expense", label: "Solo uscite" },
  { value: "tax", label: "Solo tasse" },
];

const BOOKING_STATUSES = [
  { value: "all", label: "Tutti gli stati" },
  { value: "pending", label: "In attesa" },
  { value: "confirmed", label: "Confermate" },
  { value: "completed", label: "Pagate" },
  { value: "rejected", label: "Rifiutate" },
  { value: "cancelled", label: "Annullate" },
  { value: "frozen", label: "Congelate" },
];

const EMPTY_PRESET = { name: "", source: "bookings", fmt: "xlsx", filters: {} };

async function download(path, fallbackName, method = "get") {
  const cfg = { responseType: "blob" };
  const res = method === "post" ? await api.post(path, null, cfg) : await api.get(path, cfg);
  const cd = res.headers["content-disposition"] || "";
  const m = /filename="([^"]+)"/.exec(cd);
  const filename = m ? m[1] : fallbackName;
  const url = URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function ExportTab() {
  const [bkStatus, setBkStatus] = useState("");
  const [bkFrom, setBkFrom] = useState("");
  const [bkTo, setBkTo] = useState("");
  const [finType, setFinType] = useState("");
  const [finFrom, setFinFrom] = useState("");
  const [finTo, setFinTo] = useState("");
  const [busy, setBusy] = useState(false);

  const [presets, setPresets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState(EMPTY_PRESET);

  const refresh = useCallback(async () => {
    try {
      const [p, e] = await Promise.all([
        api.get("/admin/export-presets"),
        api.get("/admin/employees").catch(() => ({ data: { employees: [] } })),
      ]);
      setPresets(p.data.presets || []);
      setEmployees(e.data.employees || []);
    } catch {
      toast.error("Errore caricamento preset");
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const exportBookings = async (fmt) => {
    setBusy(true);
    try {
      const qs = new URLSearchParams();
      qs.set("fmt", fmt);
      if (bkStatus) qs.set("status", bkStatus);
      if (bkFrom) qs.set("from", bkFrom);
      if (bkTo) qs.set("to", bkTo);
      await download(`/admin/export/bookings?${qs.toString()}`, `prenotazioni.${fmt}`);
      toast.success(`Export ${fmt.toUpperCase()} avviato`);
    } catch (e) {
      toast.error(errMsg(e, "Errore export"));
    } finally { setBusy(false); }
  };

  const exportFinance = async (fmt) => {
    setBusy(true);
    try {
      const qs = new URLSearchParams();
      qs.set("fmt", fmt);
      if (finType) qs.set("type", finType);
      if (finFrom) qs.set("from", finFrom);
      if (finTo) qs.set("to", finTo);
      await download(`/admin/export/finance?${qs.toString()}`, `bilancio.${fmt}`);
      toast.success(`Export ${fmt.toUpperCase()} avviato`);
    } catch (e) {
      toast.error(errMsg(e, "Errore export"));
    } finally { setBusy(false); }
  };

  const runPreset = async (preset) => {
    setBusy(true);
    try {
      await download(`/admin/export-presets/${preset.id}/run`, `${preset.name}.${preset.fmt}`, "post");
      toast.success(`"${preset.name}" generato`);
    } catch (e) {
      toast.error(errMsg(e, "Errore esecuzione preset"));
    } finally { setBusy(false); }
  };

  const savePreset = async () => {
    if (!draft.name.trim()) { toast.error("Dai un nome al preset"); return; }
    if (draft.source === "timesheets" && !draft.filters?.employee_id) {
      toast.error("Seleziona un dipendente per le timbrature");
      return;
    }
    try {
      const cleanFilters = {};
      Object.entries(draft.filters || {}).forEach(([k, v]) => { if (v) cleanFilters[k] = v; });
      const payload = { ...draft, filters: cleanFilters };
      await api.post("/admin/export-presets", payload);
      toast.success("Preset salvato");
      setDialogOpen(false);
      setDraft(EMPTY_PRESET);
      refresh();
    } catch (e) {
      toast.error(errMsg(e, "Errore"));
    }
  };

  const deletePreset = async (id) => {
    if (!window.confirm("Eliminare questo preset?")) return;
    try {
      await api.delete(`/admin/export-presets/${id}`);
      toast.success("Eliminato");
      refresh();
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  return (
    <div className="space-y-6" data-testid="export-tab">
      <div className="bg-white border border-[#E8E2D9] rounded-xl p-6">
        <h3 className="font-display text-xl text-[#1E3A5F] flex items-center gap-2">
          <Download className="w-5 h-5" /> Export dati
        </h3>
        <p className="text-xs text-[#635F59] mt-1">
          Scarica i tuoi dati in Excel o PDF. Crea export personalizzati riutilizzabili con un click.
        </p>
      </div>

      {/* CUSTOM PRESETS */}
      <section className="bg-white border border-[#E8E2D9] rounded-xl p-6" data-testid="export-presets-card">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <h4 className="font-display text-lg text-[#1E3A5F] flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#AC5D45]" /> Export personalizzati
            <Badge variant="outline" className="ml-2">{presets.length}</Badge>
          </h4>
          <Button
            onClick={() => { setDraft(EMPTY_PRESET); setDialogOpen(true); }}
            data-testid="preset-add"
            className="bg-[#AC5D45] hover:bg-[#8A4936] text-white"
          >
            <Plus className="w-4 h-4 mr-2" />Nuovo export personalizzato
          </Button>
        </div>
        {presets.length === 0 ? (
          <div className="text-center text-sm text-[#635F59] py-6">
            Nessun preset salvato. Crea un export personalizzato per generarlo con un click la prossima volta.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="preset-list">
            {presets.map((p) => {
              const meta = SOURCE_META[p.source] || SOURCE_META.bookings;
              const Icon = meta.icon;
              return (
                <div
                  key={p.id}
                  className="group relative bg-white border border-[#E8E2D9] hover:border-[#AC5D45] rounded-xl p-4 transition"
                  data-testid={`preset-card-${p.id}`}
                >
                  <div className={`inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br ${meta.color} text-white shadow-md mb-2`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="font-display text-base text-[#1E3A5F]">{p.name}</div>
                  <div className="text-xs text-[#635F59] mt-1">{meta.label} · {p.fmt.toUpperCase()}</div>
                  {p.filters && Object.keys(p.filters).length > 0 && (
                    <div className="text-[10px] text-[#635F59] mt-1.5 flex flex-wrap gap-1">
                      {Object.entries(p.filters).map(([k, v]) => (
                        <span key={k} className="bg-[#FDFBF7] border border-[#E8E2D9] rounded px-1.5 py-0.5">{k}: {v}</span>
                      ))}
                    </div>
                  )}
                  <div className="mt-3 flex gap-1">
                    <Button onClick={() => runPreset(p)} disabled={busy} data-testid={`preset-run-${p.id}`} size="sm" className="bg-[#1E3A5F] hover:bg-[#152A45] text-white flex-1">
                      <Play className="w-3.5 h-3.5 mr-1" /> Genera
                    </Button>
                    <Button onClick={() => deletePreset(p.id)} size="icon" variant="ghost" className="text-rose-600 hover:bg-rose-50">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* QUICK BOOKINGS */}
      <section className="bg-white border border-[#E8E2D9] rounded-xl p-6" data-testid="export-bookings-card">
        <h4 className="font-display text-lg text-[#1E3A5F] flex items-center gap-2 mb-3">
          <BookOpenCheck className="w-5 h-5" /> Prenotazioni — Export rapido
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Stato</Label>
            <Select value={bkStatus} onValueChange={(v) => setBkStatus(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="exp-bk-status"><SelectValue placeholder="Tutti" /></SelectTrigger>
              <SelectContent>{BOOKING_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Check-in dal</Label>
            <DateInputIT value={bkFrom} onChange={(e) => setBkFrom(e.target.value)} data-testid="exp-bk-from" />
          </div>
          <div>
            <Label className="text-xs">Check-in al</Label>
            <DateInputIT value={bkTo} onChange={(e) => setBkTo(e.target.value)} data-testid="exp-bk-to" />
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <Button onClick={() => exportBookings("xlsx")} disabled={busy} data-testid="exp-bk-xlsx" className="bg-emerald-700 hover:bg-emerald-800 text-white">
            <FileSpreadsheet className="w-4 h-4 mr-2" />Excel (XLSX)
          </Button>
          <Button onClick={() => exportBookings("pdf")} disabled={busy} data-testid="exp-bk-pdf" className="bg-[#AC5D45] hover:bg-[#8A4936] text-white">
            <FileDown className="w-4 h-4 mr-2" />PDF
          </Button>
        </div>
      </section>

      {/* QUICK FINANCE */}
      <section className="bg-white border border-[#E8E2D9] rounded-xl p-6" data-testid="export-finance-card">
        <h4 className="font-display text-lg text-[#1E3A5F] flex items-center gap-2 mb-3">
          <Wallet className="w-5 h-5" /> Bilancio — Export rapido
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Tipo</Label>
            <Select value={finType} onValueChange={(v) => setFinType(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="exp-fin-type"><SelectValue placeholder="Tutti" /></SelectTrigger>
              <SelectContent>{FINANCE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Dal</Label>
            <DateInputIT value={finFrom} onChange={(e) => setFinFrom(e.target.value)} data-testid="exp-fin-from" />
          </div>
          <div>
            <Label className="text-xs">Al</Label>
            <DateInputIT value={finTo} onChange={(e) => setFinTo(e.target.value)} data-testid="exp-fin-to" />
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <Button onClick={() => exportFinance("xlsx")} disabled={busy} data-testid="exp-fin-xlsx" className="bg-emerald-700 hover:bg-emerald-800 text-white">
            <FileSpreadsheet className="w-4 h-4 mr-2" />Excel (XLSX)
          </Button>
          <Button onClick={() => exportFinance("pdf")} disabled={busy} data-testid="exp-fin-pdf" className="bg-[#AC5D45] hover:bg-[#8A4936] text-white">
            <FileDown className="w-4 h-4 mr-2" />PDF
          </Button>
        </div>
      </section>

      <div className="text-xs text-[#635F59] px-1">
        Le timbrature singole si scaricano dalla scheda <strong>Dipendenti</strong>.
        Per export ricorrenti, crea un preset cliccando "Nuovo export personalizzato".
      </div>

      {/* CREATE PRESET DIALOG */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="preset-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#AC5D45]" /> Nuovo export personalizzato</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Nome *</Label>
              <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Es. Prenotazioni Estate 2026" data-testid="preset-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Sorgente dati</Label>
                <Select value={draft.source} onValueChange={(v) => setDraft({ ...draft, source: v, filters: {} })}>
                  <SelectTrigger data-testid="preset-source"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bookings">Prenotazioni</SelectItem>
                    <SelectItem value="finance">Bilancio / Movimenti</SelectItem>
                    <SelectItem value="timesheets">Timbrature dipendenti</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Formato</Label>
                <Select value={draft.fmt} onValueChange={(v) => setDraft({ ...draft, fmt: v })}>
                  <SelectTrigger data-testid="preset-fmt"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="xlsx">Excel (XLSX)</SelectItem>
                    <SelectItem value="pdf">PDF</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {draft.source === "bookings" && (
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Stato</Label>
                  <Select value={draft.filters.status || ""} onValueChange={(v) => setDraft({ ...draft, filters: { ...draft.filters, status: v === "all" ? "" : v } })}>
                    <SelectTrigger><SelectValue placeholder="Tutti" /></SelectTrigger>
                    <SelectContent>{BOOKING_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Dal</Label>
                  <DateInputIT value={draft.filters.from || ""} onChange={(e) => setDraft({ ...draft, filters: { ...draft.filters, from: e.target.value } })} />
                </div>
                <div>
                  <Label className="text-xs">Al</Label>
                  <DateInputIT value={draft.filters.to || ""} onChange={(e) => setDraft({ ...draft, filters: { ...draft.filters, to: e.target.value } })} />
                </div>
              </div>
            )}

            {draft.source === "finance" && (
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Tipo</Label>
                  <Select value={draft.filters.type || ""} onValueChange={(v) => setDraft({ ...draft, filters: { ...draft.filters, type: v === "all" ? "" : v } })}>
                    <SelectTrigger><SelectValue placeholder="Tutti" /></SelectTrigger>
                    <SelectContent>{FINANCE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Dal</Label>
                  <DateInputIT value={draft.filters.from || ""} onChange={(e) => setDraft({ ...draft, filters: { ...draft.filters, from: e.target.value } })} />
                </div>
                <div>
                  <Label className="text-xs">Al</Label>
                  <DateInputIT value={draft.filters.to || ""} onChange={(e) => setDraft({ ...draft, filters: { ...draft.filters, to: e.target.value } })} />
                </div>
              </div>
            )}

            {draft.source === "timesheets" && (
              <div>
                <Label className="text-xs">Dipendente *</Label>
                <Select value={draft.filters.employee_id || ""} onValueChange={(v) => setDraft({ ...draft, filters: { ...draft.filters, employee_id: v } })}>
                  <SelectTrigger data-testid="preset-emp"><SelectValue placeholder="Seleziona dipendente" /></SelectTrigger>
                  <SelectContent>
                    {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {employees.length === 0 && <p className="text-xs text-[#635F59] mt-1">Nessun dipendente. Crea prima un dipendente nella scheda Dipendenti.</p>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
            <Button onClick={savePreset} data-testid="preset-save" className="bg-[#AC5D45] hover:bg-[#8A4936] text-white">
              <Save className="w-4 h-4 mr-1" />Salva preset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
